import useIsMounted from '@/hooks/useIsMounted/useIsMounted';
import { useQueryParams } from '@/hooks/useQueryParams/useQueryParams';
import { useSafeLayoutEffect } from '@/hooks/useSafeLayoutEffect/useSafeLayoutEffect';
import useSearchItems from '@/hooks/useSearchItems/useSearchItems';
import { useSelected } from '@/hooks/useSelected/useSelected';
import { JobPosting } from '@/page-templates/JobDetailsPage/interfaces';
import { translateJobKey } from '@uikit/helpers/translateJobKey';
import { firstIfArray } from '@uikit/utils/firstIfArray';
import React from 'react';
import getValidQueryFilterItems from '../../../helpers/getValidQueryFilterItems';
import assetPath from '../../helpers/assetPath';
import { ButtonAlign } from '../Button/consts';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ClickableAction } from '../Clickable/consts';
import Container from '../Container/Container';
import { ContainerChildrenSpace, ContainerSize } from '../Container/consts';
import Filter from '../Filter/Filter';
import { FilterItems } from '../Filter/interfaces';
import { FormFieldElement } from '../FormField/interfaces';
import Headline from '../Headline/Headline';
import { HeadlineColor } from '../Headline/consts';
import JobListWithPagination from '../JobListWithPagination/JobListWithPagination';
import { MultiSelectLabel } from '../MultiSelect/interfaces';
import NoSearchResult from '../NoSearchResult/NoSearchResult';
import Section from '../Section/Section';
import { SectionBackgroundColor } from '../Section/consts';
import type {
  JobSearchTemplateProps,
  MainCategoryItem,
} from './JobSearchContent.interfaces';
import styles from './JobSearchPageContent.module.scss';
import { extendMainCategoriesBySynonyms } from './helpers';

const jobIdMatcher = /J\d{7}/g; // J2022001 | J YEAR NUMBER

const sectionOverlay = `${assetPath}/assets/overlays/overlay_bubbles.svg`;

const searchTermWithIncludeMatch = (searchTerm: string) => {
  return searchTerm.match(jobIdMatcher)?.length === 0
    ? searchTerm
    : `'${searchTerm}`; // use ' for include-match search to find intern jobIds
};

const JobSearchPageContent = ({
  jobs,
  mainCategories = [],
  timeTypes = [],
  isFavoriteJob,
  toggleFavoriteJob,
}: JobSearchTemplateProps) => {
  const isRouterReady = useIsMounted();

  const { params: queryParams, set: setQueryParams } = useQueryParams();

  const [searchTerm, setSearchTerm] = React.useState('');

  const selectedMainCategoriesProps = useSelected();

  const selectedTimeTypesProps = useSelected();

  const searchJobs = useSearchItems(jobs, [
    'title',
    'jobId',
    'description',
    'categories',
    'timeType',
    'company',
    'location',
  ]);

  const onSearchTermChange = ($event: React.ChangeEvent<FormFieldElement>) => {
    setSearchTerm(() => $event.target.value);
  };

  const mainCategoryLabels = mainCategories.map(
    (mainCategory: MainCategoryItem) => {
      const jobsOfCategory = jobs.filter((job) =>
        job.categories?.every((category) => {
          return extendMainCategoriesBySynonyms([mainCategory.label]).includes(
            category
          );
        })
      );
      const filterItemLabel: MultiSelectLabel = {
        label: mainCategory.label,
        count: jobsOfCategory.length,
        category: mainCategory?.category,
        type: mainCategory?.type,
      };
      return filterItemLabel;
    }
  );

  const timeTypeLabels = ['fullTime', 'partTime'].map((timeType) => {
    const jobsByTimeType = jobs.filter((job) =>
      job.timeTypes?.includes(timeType)
    );

    return {
      label: translateJobKey(timeType),
      count: jobsByTimeType.length,
    };
  });

  const filterItems: FilterItems[] = [
    {
      label: 'Arbeitswelten',
      items: mainCategoryLabels,
      ...selectedMainCategoriesProps,
    },
    {
      label: 'Arbeitszeit',
      items: timeTypeLabels,
      ...selectedTimeTypesProps,
    },
  ];

  const onFilterReset = () => {
    selectedMainCategoriesProps.resetSelected();
    selectedTimeTypesProps.resetSelected();
    setSearchTerm(() => '');

    setQueryParams({
      arbeitswelten: undefined,
      arbeitszeit: undefined,
      suche: undefined,
    });
  };

  const getResultsByCategories = (jobPostings: JobPosting[]) => {
    const selectedMainCategoriesExtended = extendMainCategoriesBySynonyms(
      selectedMainCategoriesProps.selected
    );

    return jobPostings
      .filter((job) => {
        if (selectedMainCategoriesExtended.length === 0) {
          return true;
        }
        const category = (job.categories && job.categories[0]) || '';

        return selectedMainCategoriesExtended.includes(category);
      })
      .filter((job) => {
        if (selectedTimeTypesProps.selected.length === 0) {
          return true;
        }

        return job.timeTypes?.some(
          (timeType) =>
            timeType &&
            selectedTimeTypesProps.selected.includes(translateJobKey(timeType))
        );
      });
  };

  const handleFilterApplyClick = () => {
    setQueryParams({
      arbeitswelten:
        selectedMainCategoriesProps.selected.length > 0
          ? [...selectedMainCategoriesProps.selected]
          : undefined,
      arbeitszeit:
        selectedTimeTypesProps.selected.length > 0
          ? [...selectedTimeTypesProps.selected]
          : undefined,
      suche: searchTerm || undefined,
    });
  };

  const termSearchResults = searchTerm
    ? searchJobs(searchTermWithIncludeMatch(searchTerm))
    : jobs;
  const shownResults = getResultsByCategories(termSearchResults as any); // refactor here type

  useSafeLayoutEffect(() => {
    if (typeof queryParams.arbeitswelten !== 'undefined') {
      selectedMainCategoriesProps.setSelected(
        getValidQueryFilterItems(
          queryParams.arbeitswelten,
          mainCategories.map(({ label }) => label)
        ) ?? []
      );
    }

    if (typeof queryParams.arbeitszeit !== 'undefined') {
      selectedTimeTypesProps.setSelected(
        getValidQueryFilterItems(queryParams.arbeitszeit, timeTypes) ?? []
      );
    }

    if (typeof queryParams.suche !== 'undefined') {
      setSearchTerm(firstIfArray(queryParams.suche));
    }
  }, []);

  React.useEffect(() => {
    if (!isRouterReady) {
      return;
    }

    setQueryParams({
      arbeitswelten:
        selectedMainCategoriesProps.selected.length > 0
          ? [...selectedMainCategoriesProps.selected]
          : undefined,
    });
  }, [isRouterReady, selectedMainCategoriesProps.selected, setQueryParams]);

  React.useEffect(() => {
    if (!isRouterReady) {
      return;
    }

    setQueryParams({
      arbeitszeit:
        selectedTimeTypesProps.selected.length > 0
          ? [...selectedTimeTypesProps.selected]
          : undefined,
    });
  }, [isRouterReady, selectedTimeTypesProps.selected, setQueryParams]);

  return (
    <>
      <Section
        bgColor={SectionBackgroundColor.Gradient}
        overlayImageUrl={sectionOverlay}
        resetTopSpace
      >
        <Container
          size={ContainerSize.TinyPlus}
          spaceBetweenChildren={ContainerChildrenSpace.None}
          className={styles.filterContainer}
        >
          <Headline headlineColor={HeadlineColor.Light} level={2}>
            Ein Unternehmen, viele Möglichkeiten. Welche ergreifst du?
          </Headline>
        </Container>
        <Container className={styles.searchbarContainer}>
          <Filter
            onSearchTermChange={onSearchTermChange}
            onFilterReset={onFilterReset}
            filterItems={filterItems}
            isBoxOnGradient={true}
            searchTerm={searchTerm}
            onFilterApplyClick={handleFilterApplyClick}
          />
        </Container>
      </Section>

      <Section sectionId="jobliste" resetTopSpace>
        <Container
          spaceBetweenChildren={ContainerChildrenSpace.None}
          className={styles.searchHeadlineContainer}
        >
          <div className={styles.searchHeadline}>
            {shownResults.length} freie Jobs
          </div>
        </Container>
        {shownResults.length > 0 ? (
          <Container className={styles.searchResultContainer}>
            <JobListWithPagination
              jobs={shownResults}
              toggleFavorite={toggleFavoriteJob}
              isFavorite={isFavoriteJob}
              hasItemPadding
              itemsPerPage={10}
            />
          </Container>
        ) : (
          <Container size={ContainerSize.Default} spaceBottom={0}>
            <NoSearchResult
              headline="Wir haben leider keine passenden Jobs gefunden."
              iconVariant="user/monkey-blind"
              subtext="Probier es doch mal mit einem anderen Suchbegriff oder anderen Filtern. "
            >
              <div className={styles.jobAlertBox}>
                <h3 className={styles.jobAlertBoxTitle}>
                  Melde dich für unseren Job-Alert an!
                </h3>
                <div className={styles.jobAlertBoxSubtitle}>
                  Wir schicken dir eine E-Mail, wenn wir neue Jobs für dich
                  haben.
                </div>
                <ButtonLink
                  actionType={ClickableAction.Modal}
                  align={ButtonAlign.Center}
                  disabled={false}
                  icon="action/arrow-right"
                  iconPosition="left"
                  modallink="job-alert"
                >
                  Jetzt anmelden
                </ButtonLink>
              </div>
            </NoSearchResult>
          </Container>
        )}
      </Section>
    </>
  );
};

export default JobSearchPageContent;
